import { TZDate } from "@date-fns/tz";
import {
  startOfDay,
  endOfDay,
  endOfWeek,
  startOfWeek,
  addDays,
  startOfMonth,
  endOfMonth,
  subDays,
  Locale,
  format,
  addMinutes
} from "date-fns";
import i18next, { TFunction } from "i18next";

/**
 * Strips HTML tags from a string and returns plain text
 * Useful for displaying Quill editor content in calendar events
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) return "";

  // Create a temporary div element to parse HTML
  const temp = document.createElement("div");
  temp.innerHTML = html;

  // Get text content and clean up extra whitespace
  return temp.textContent?.trim() || temp.innerText?.trim() || "";
};

/**
 * Truncates text to a specified length and adds ellipsis if needed
 * Useful for displaying descriptions in calendar event previews
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (!text || text.length <= maxLength) return text;

  return text.substring(0, maxLength).trim() + "...";
};

/**
 * Converts HTML description to plain text with truncation for calendar display
 */
export const getDisplayDescription = (htmlDescription: string, maxLength: number = 100): string => {
  const plainText = stripHtmlTags(htmlDescription);
  return truncateText(plainText, maxLength);
};

/**
 * Creates properly formatted all-day event times
 * For allDay events spanning multiple days:
 * - Start time is set to 00:00:00 of the start date
 * - End time is set to 23:59:59 of the actual end date
 */
export const createAllDayEventTimes = (
  startDate: Date,
  durationDays: number = 1
): { start: Date; end: Date } => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(startDate);
  end.setDate(end.getDate() + durationDays - 1);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

export const getDateRangeForView = (date: Date, view: string) => {
  const currentDate = new Date(date);

  switch (view) {
    case "month": {
      const startOfMonthDate = startOfMonth(currentDate);
      const endOfMonthDate = endOfMonth(currentDate);
      // Include previous and next week for month view
      const startDate = subDays(startOfMonthDate, 7);
      const endDate = addDays(endOfMonthDate, 7);
      return { startDate, endDate };
    }
    case "week":
    case "work_week": {
      const startDate = startOfWeek(currentDate);
      const endDate = endOfWeek(currentDate);
      return { startDate, endDate };
    }
    case "day": {
      const startDate = startOfDay(currentDate);
      const endDate = endOfDay(currentDate);
      return { startDate, endDate };
    }
    case "timeline": {
      const startDate = startOfWeek(currentDate);
      const endDate = endOfWeek(currentDate);
      return { startDate, endDate };
    }
    default:
      return { startDate: currentDate, endDate: currentDate };
  }
};
/**
 * Processes an event to ensure proper time handling based on allDay flag
 */
export const processEventTimes = <
  T extends { start?: Date | string; end?: Date | string; allDay?: boolean; durationDays?: number }
>(
  event: T
): T => {
  if (!event.allDay || !event.start) {
    return event;
  }

  const startDate = typeof event.start === "string" ? new Date(event.start) : event.start;
  const endDate = event.end
    ? typeof event.end === "string"
      ? new Date(event.end)
      : event.end
    : startDate;

  return {
    ...event,
    start: startDate,
    end: endDate
  };
};
export type WeekDay = {
  key: string;
  text: string;
  name: string;
};
export const getOrdinalNumber = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Helper function để lấy số thứ tự (1st, 2nd, 3rd, 4th)
export const getOrdinal = (n: number): string => {
  if (n === 5) {
    return "last";
  }
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getWeekOfMonth = (date: Date): number => {
  return Math.ceil(date.getDate() / 7);
};

export const getMonthlyWeekdayPattern = (
  date: Date,
  weekDays: WeekDay[]
): { weekNum: number; dayName: string } => {
  return {
    weekNum: getWeekOfMonth(date),
    dayName: weekDays[date.getDay()].name
  };
};

export function countSpecificDayInMonth(year: number, month: number, dayOfWeek: number) {
  let count = 0;
  for (let i = 1; i <= new Date(year, month, 0).getDate(); i++) {
    const date = new Date(year, month - 1, i);
    if (date.getDay() === dayOfWeek) {
      count++;
    }
  }
  return count;
}

export const normalizeVersion = (version: string): string => {
  try {
    const date = new Date(version);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  } catch (e) {
    console.error("Error normalizing version:", e);
  }
  return version;
};
export const getMonthKey = (date: string | Date): string => {
  if (typeof date === "string") {
    date = new Date(date);
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export const getMonthsInRange = (startDate: Date, endDate: Date): string[] => {
  const months: string[] = [];
  const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  while (current <= end) {
    months.push(getMonthKey(current));
    current.setMonth(current.getMonth() + 1);
  }

  return months;
};

export const getMonthRange = (month: string): { startTime: Date; endTime: Date } => {
  const [year, monthNum] = month.split("-").map(Number);
  const startTime = new Date(year, monthNum - 1, 1);
  const endTime = new Date(year, monthNum, 0, 23, 59, 59, 999);
  return { startTime, endTime };
};

const getAdjacentMonth = (month: string, offset: number): string => {
  const [year, monthNum] = month.split("-").map(Number);
  const date = new Date(year, monthNum - 1 + offset, 1);
  return getMonthKey(date);
};

export const getPreviousMonth = (month: string) => getAdjacentMonth(month, -1);
export const getNextMonth = (month: string) => getAdjacentMonth(month, 1);

export const statusOptions = (t: TFunction) => [
  { label: t("newEvent.option-free", "Free"), value: "free" },
  {
    label: t("newEvent.option-workingelsewhere", "Working elsewhere"),
    value: "workingelsewhere"
  },
  { label: t("newEvent.option-tentative", "Tentative"), value: "tentative" },
  { label: t("newEvent.option-busy", "Busy"), value: "busy" },
  { label: t("newEvent.option-outofoffice", "Out of office"), value: "oof" }
];

export const isValidEmail = (email: string): boolean => {
  if (!email) return false;

  const trimmedEmail = email.trim();

  // Reject emails with trailing dots
  if (trimmedEmail.endsWith(".")) {
    return false;
  }

  // Basic email regex: local@domain.tld
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(trimmedEmail);
};

export const showAsMapping: Record<string, string> = {
  free: "Free",
  workingelsewhere: "Working Elsewhere",
  tentative: "Tentative",
  busy: "Busy",
  oof: "Out Of Office"
};
export const getOffsetBetweenClientAndSettingTimezone = (
  date: Date,
  timezone?: string,
  isReverse: boolean = false,
  exactTimeSpan: boolean = false
) => {
  const clientOffset = date.getTimezoneOffset();
  const targetDate = exactTimeSpan
    ? new TZDate(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        timezone
      )
    : new TZDate(date, timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const targetOffset = targetDate.getTimezoneOffset();
  return isReverse
    ? addMinutes(date, targetOffset - clientOffset)
    : addMinutes(date, clientOffset - targetOffset);
};
